"use client";

// TODO: Remove this component after ui-kit team adds InputCode to @mo/ui exports

import type {
  ChangeEventHandler,
  ClipboardEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
} from "react";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import clsx from "clsx";

import "./InputCode.scss";

export interface InputCodeProps {
  value?: string,
  onChange?(value: string): void,
  onComplete?(value: string): void,
  onPasteComplete?(value: string): void,
  charsCount?: number,
  gap?: number,
  isError?: boolean,
  isLoading?: boolean,
  isSuccess?: boolean,
  disabled?: boolean,
  autoFocus?: boolean,
  type?: "text" | "password",
  className?: string,
}

const BLOCK = "mo-input-code";

const b = (element?: string | Record<string, boolean | undefined>,
  mods?: Record<string, boolean | undefined> | string) => {
  if (typeof element === "object") {
    const modClasses = Object.entries(element)
      .filter(([ , value ]) => value)
      .map(([ key ]) => `${BLOCK}_${key}`);
    return clsx(
      BLOCK, ...modClasses, mods,
    );
  }

  if (element) {
    const elementClass = `${BLOCK}__${element}`;
    if (typeof mods === "object") {
      const modClasses = Object.entries(mods)
        .filter(([ , value ]) => value)
        .map(([ key ]) => `${BLOCK}__${element}_${key}`);
      return clsx(elementClass, ...modClasses);
    }
    return clsx(elementClass, mods);
  }

  return BLOCK;
};

export const InputCode = forwardRef<HTMLInputElement, InputCodeProps>(function InputCode({
  autoFocus = true,
  charsCount = 6,
  className,
  disabled,
  gap = 12,
  isError,
  isLoading,
  isSuccess,
  onChange,
  onComplete,
  onPasteComplete,
  type = "text",
  value: valueFromProps,
},
ref) {
  const [ otpValue, setOtpValue ] = useState<string[]>([]);
  const [ focusedIndex, setFocusedIndex ] = useState<number | null>(null);
  const isControlled = valueFromProps !== undefined;
  const syncedValue = isControlled ?
    String(valueFromProps).split("") :
    otpValue;

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const chars = useMemo(() => Array.from({ length: charsCount }, (_, i) => i), [ charsCount ]);

  // Set ref for external access
  useEffect(() => {
    if (ref && focusedIndex !== null) {
      const activeInput = inputRefs.current[ focusedIndex ] ?? null;
      if (typeof ref === "function") {
        ref(activeInput);
      } else {
        ref.current = activeInput;
      }
    }
  }, [ ref, focusedIndex ]);

  // Auto focus on mount
  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        inputRefs.current[ 0 ]?.focus();
        setFocusedIndex(0);
      }, 0);
    }
  }, [ autoFocus ]);

  useEffect(() => {
    if (syncedValue.length === charsCount && syncedValue.every(Boolean)) {
      onComplete?.(syncedValue.join(""));
    }
  }, [
    syncedValue,
    charsCount,
    onComplete,
  ]);

  const focusIndex = useCallback((index: number) => {
    const clampedIndex = Math.max(0, Math.min(index, charsCount - 1));
    setFocusedIndex(clampedIndex);
    setTimeout(() => {
      inputRefs.current[ clampedIndex ]?.focus();
    }, 0);
  }, [ charsCount ]);

  const handleChange = useCallback((index: number): ChangeEventHandler<HTMLInputElement> => (e) => {
    const newValue = e.target.value.replace(/\D/g, "");
    const currentChar = syncedValue[ index ] || "";

    const digit = newValue.length > 1 ?
      newValue.split("").find((char) => char !== currentChar) || newValue.slice(-1) :
      newValue;

    if (!digit) return;

    if (isControlled) {
      const currentArr = String(valueFromProps).split("");
      // Ensure array is long enough
      while (currentArr.length <= index) {
        currentArr.push("");
      }
      currentArr[ index ] = digit;
      const newValue = currentArr.slice(0, charsCount).join("");
      onChange?.(newValue);
    } else {
      setOtpValue((prev) => {
        const newArr = [ ...prev ];
        while (newArr.length <= index) {
          newArr.push("");
        }
        newArr[ index ] = digit;
        return newArr;
      });
    }

    // Move to next input
    if (index < charsCount - 1) {
      focusIndex(index + 1);
    }
  }, [
    isControlled,
    valueFromProps,
    charsCount,
    onChange,
    focusIndex,
    syncedValue,
  ]);

  const handleKeyDown = useCallback((index: number): KeyboardEventHandler<HTMLInputElement> => (e) => {
    if (e.key === "Backspace") {
      e.preventDefault();

      const currentValue = syncedValue[ index ];

      if (isControlled) {
        const currentArr = String(valueFromProps).split("");
        while (currentArr.length < charsCount) {
          currentArr.push("");
        }
        if (currentValue) {
          // Clear current cell
          currentArr[ index ] = "";
          onChange?.(currentArr.join(""));
        } else if (index > 0) {
          // Clear previous cell and move focus
          currentArr[ index - 1 ] = "";
          onChange?.(currentArr.join(""));
          focusIndex(index - 1);
        }
      } else if (currentValue) {
        setOtpValue((prev) => {
          const newArr = [ ...prev ];
          newArr[ index ] = "";
          return newArr;
        });
      } else if (index > 0) {
        setOtpValue((prev) => {
          const newArr = [ ...prev ];
          newArr[ index - 1 ] = "";
          return newArr;
        });
        focusIndex(index - 1);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusIndex(index - 1);
    } else if (e.key === "ArrowRight" && index < charsCount - 1) {
      e.preventDefault();
      focusIndex(index + 1);
    }
  }, [
    syncedValue,
    isControlled,
    valueFromProps,
    onChange,
    focusIndex,
    charsCount,
  ]);

  const handlePaste: ClipboardEventHandler<HTMLInputElement> = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    const digits = pasted.replace(/\D/g, "").slice(0, charsCount);

    if (digits) {
      if (isControlled) {
        onChange?.(digits);
      } else {
        setOtpValue(digits.split(""));
      }
      focusIndex(Math.min(digits.length, charsCount - 1));

      if (digits.length === charsCount) {
        onPasteComplete?.(digits);
      }
    }
  }, [
    charsCount,
    isControlled,
    onChange,
    onPasteComplete,
    focusIndex,
  ]);

  const handleCellClick = useCallback((index: number): MouseEventHandler<HTMLDivElement> => (e) => {
    e.stopPropagation();
    focusIndex(index);
  }, [ focusIndex ]);

  const handleFocus = useCallback((index: number) => (_e: React.FocusEvent<HTMLInputElement>) => {
    setFocusedIndex(index);
  }, []);

  const handleBlur = useCallback(() => {
    setFocusedIndex(null);
  }, []);

  return (
    <div
      className={
        b({
          error: isError,
          loading: isLoading,
          success: isSuccess,
        }, className)
      }
      data-amount={charsCount}
    >
      {
        chars.map((idx) => {
          const isMiddle = charsCount % 2 === 0 && charsCount / 2 === idx + 1;
          const inputValue = syncedValue[ idx ] ?? "";
          const showDot = type === "password" && !!inputValue;

          return (
            <div
              key={idx}
              className={
                b("item", {
                  "middle": isMiddle,
                  "password": type === "password",
                  "password-filled": showDot && !isLoading,
                  "filled": !!inputValue,
                })
              }
              style={
                isMiddle ?
                  { marginRight: gap } :
                  undefined
              }
              onClick={handleCellClick(idx)}
            >
              <input
                ref={
                  (el) => {
                    inputRefs.current[ idx ] = el;
                  }
                }
                autoComplete="one-time-code"
                className={b("input")}
                disabled={disabled || isLoading}
                inputMode="numeric"
                maxLength={2}
                type="text"
                value={
                  showDot ?
                    "•" :
                    inputValue
                }
                onBlur={handleBlur}
                onChange={handleChange(idx)}
                onFocus={handleFocus(idx)}
                onKeyDown={handleKeyDown(idx)}
                onPaste={handlePaste}
              />
            </div>
          );
        })
      }
    </div>
  );
});
